import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiErrorCodes, parseApiResponseError } from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import {
  ClientsConfigResponse,
  LoginError as LoginApiError,
  LoginErrorCodiciErroreEnum,
  Rubriche,
  AcceptanceDocumentType,
  CurrentDocumentVersion,
  DocumentAcceptanceRequest,
} from '../../../../api/generated/apc/api';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  getStatusUtenza,
  updateIsFiduciaria,
  updateProspectToken,
  updateToken,
} from '../../../data';
import {
  LoginError,
  LoginRequestParams,
  LoginState,
  GetRubricheError,
  GetConfigError,
} from './login-types';
import { verifyLoginFields } from './login-utils';
import { updateIsProspect, updateReferral } from '../../../data';
import { getAPIRequestHeaders } from '../../../../utils/api-utils';

const initialState: LoginState = {
  loginRequestStatus: 'idle',
  getRubricheStatus: 'idle',
  codiceCliente: undefined,
  rubriche: [],
  codiceUtente: undefined,
  migrato: true,
  codiceProspect: undefined,
  codiceProspectRappresentante: undefined,
  tokenProspect: undefined,
  config: undefined,
  currentDocumentVersion: undefined,
  documentAcceptanceStatus: 'idle',
  acceptDocumentStatus: 'idle',
};

export const login = createAsyncThunk<
  string,
  LoginRequestParams,
  AsyncThunkConfig<LoginError>
>('login', async (params: LoginRequestParams, thunkApi) => {
  try {
    const validationError = verifyLoginFields(params);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 2000);
    });
    const response = await apiManager.loginApi.login2({ ...params });
    const token = response.data?.token;
    const prospectToken = response.data?.prospectToken;
    await thunkApi.dispatch(setCodiceUtente(params.username));
    await thunkApi.dispatch(setCodiceProspect(response?.data.codiceProspect));
    await thunkApi.dispatch(setTokenProspect(response?.data.prospectToken));
    await thunkApi.dispatch(
      setCodiceProspectRappresentante(
        response?.data.codiceProspectRappresentante
      )
    );
    await thunkApi.dispatch(updateToken(token));
    await thunkApi.dispatch(updateProspectToken(prospectToken));
    if (response.data.primoAccesso) {
      await thunkApi.dispatch(updateFirstAccess(response.data?.primoAccesso));
    }
    if (response.data.isProspect) {
      await thunkApi.dispatch(updateIsProspect(response.data.isProspect));
    }
    if (response.data.referral) {
      await thunkApi.dispatch(updateReferral(response.data.referral));
    }
    if (response.data.fiduciaria) {
      await thunkApi.dispatch(updateIsFiduciaria(response.data.fiduciaria));
    }
    return token;
  } catch (error) {
    const apiError = parseApiResponseError<LoginApiError>(error);
    const errorCodes: any = apiError.payload?.codiciErrore;
    if (apiError.responseErrorCode === ApiErrorCodes.TOO_MANY_REQUESTS) {
      return thunkApi.rejectWithValue('TOO_MANY_REQUESTS');
    } else if (
      errorCodes &&
      errorCodes.indexOf(LoginErrorCodiciErroreEnum.CredenzialiNonValide) >= 0
    ) {
      return thunkApi.rejectWithValue('INVALID_CREDENTIALS');
    } else if (
      errorCodes &&
      errorCodes.indexOf(LoginErrorCodiciErroreEnum.UtenzaBloccata) >= 0
    ) {
      return thunkApi.rejectWithValue('USER_BLOCKED');
    } else if (
      errorCodes &&
      errorCodes.indexOf(LoginErrorCodiciErroreEnum.UtenzaNonMigrata) >= 0
    ) {
      return thunkApi.rejectWithValue('NOT_MIGRATED_USER');
    } else if (
      errorCodes &&
      errorCodes.indexOf(LoginErrorCodiciErroreEnum.CampiRichiesti) >= 0
    ) {
      return thunkApi.rejectWithValue('FILL_ALL_FIELDS');
    } else if (errorCodes && errorCodes.indexOf('CODICE_UTENTE_ERRATO') >= 0) {
      return thunkApi.rejectWithValue('CODICE_UTENTE_ERRATO');
    } else if (errorCodes && errorCodes.indexOf('PASSWORD_ERRATA') >= 0) {
      return thunkApi.rejectWithValue('PASSWORD_ERRATA');
    } else {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
});

export const getRubriche = createAsyncThunk<
  Rubriche,
  void,
  AsyncThunkConfig<GetRubricheError>
>('get-rubriche', async (_, thunkApi) => {
  try {
    const res = await apiManager.fiduciarieApi.getRubriche(
      thunkApi.getState().features.login.codiceUtente,
      getAPIRequestHeaders(thunkApi)
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const getConfig = createAsyncThunk<
  ClientsConfigResponse,
  void,
  AsyncThunkConfig<GetConfigError>
>('get-config', async (_, thunkApi) => {
  try {
    const res = await apiManager.config.getClientsConfig();
    if (res.data?.status?.code === '0') {
      return res.data;
    } else {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const getCurrentDocumentVersion = createAsyncThunk<
  CurrentDocumentVersion | null,
  AcceptanceDocumentType,
  AsyncThunkConfig<string>
>('get-current-document-version', async (documentType, thunkApi) => {
  try {
    const res =
      await apiManager.documentAcceptanceApi.getCurrentDocumentVersion(
        documentType,
        getAPIRequestHeaders(thunkApi)
      );
    return res.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const acceptDocumentVersion = createAsyncThunk<
  void,
  DocumentAcceptanceRequest,
  AsyncThunkConfig<string>
>('accept-document-version', async (request, thunkApi) => {
  try {
    await apiManager.documentAcceptanceApi.acceptDocumentVersion(
      request,
      getAPIRequestHeaders(thunkApi)
    );
    return null;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

const loginSlice = createSlice({
  name: 'features/login',
  initialState,
  reducers: {
    resetLoginState(state) {
      state.loginError = undefined;
      state.loginRequestStatus = 'idle';
      state.isFirstAccess = undefined;
    },
    updateFirstAccess(state, action: PayloadAction<boolean>) {
      state.isFirstAccess = action.payload;
    },
    updateMigrato(state, action: PayloadAction<boolean>) {
      state.migrato = action.payload;
    },
    setCodiceUtente(state, action: PayloadAction<string>) {
      state.codiceUtente = action.payload;
    },
    setCodiceProspect(state, action) {
      state.codiceProspect = action.payload;
    },
    setTokenProspect(state, action) {
      state.tokenProspect = action.payload;
    },
    setCodiceProspectRappresentante(state, action) {
      state.codiceProspectRappresentante = action.payload;
    },
    setCurrentDocumentVersion(
      state,
      action: PayloadAction<CurrentDocumentVersion | undefined>
    ) {
      state.currentDocumentVersion = action.payload;
    },
    resetDocumentAcceptanceStatus(state) {
      state.documentAcceptanceStatus = 'idle';
      state.acceptDocumentStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.loginRequestStatus = 'loading';
    });
    builder.addCase(login.fulfilled, (state) => {
      state.loginRequestStatus = 'succeeded';
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loginError = action.payload || 'GENERIC_ERROR';
      state.loginRequestStatus = 'failed';
    });
    builder.addCase(getStatusUtenza.fulfilled, (state, action) => {
      if (action.payload?.codiceCliente) {
        state.codiceUtente = action.payload.codiceUtente;
      }
    });
    builder.addCase(getRubriche.pending, (state) => {
      state.getRubricheStatus = 'loading';
    });
    builder.addCase(getRubriche.fulfilled, (state, action) => {
      state.getRubricheStatus = 'succeeded';
      state.rubriche = action.payload.rubriche;
      state.codiceCliente = action.payload.codiceCliente;
    });
    builder.addCase(getRubriche.rejected, (state) => {
      state.getRubricheStatus = 'failed';
    });
    builder.addCase(getConfig.fulfilled, (state, action) => {
      state.config = action.payload.data;
    });
    builder.addCase(getConfig.rejected, (state) => {
      state.config = { maintenance: true };
    });
    builder.addCase(getCurrentDocumentVersion.pending, (state) => {
      state.documentAcceptanceStatus = 'loading';
    });
    builder.addCase(getCurrentDocumentVersion.fulfilled, (state, action) => {
      state.documentAcceptanceStatus = 'succeeded';
      state.currentDocumentVersion = action.payload ?? undefined;
    });
    builder.addCase(getCurrentDocumentVersion.rejected, (state) => {
      state.documentAcceptanceStatus = 'failed';
    });
    builder.addCase(acceptDocumentVersion.pending, (state) => {
      state.acceptDocumentStatus = 'loading';
    });
    builder.addCase(acceptDocumentVersion.fulfilled, (state) => {
      state.acceptDocumentStatus = 'succeeded';
      if (state.currentDocumentVersion) {
        state.currentDocumentVersion.accepted = true;
      }
    });
    builder.addCase(acceptDocumentVersion.rejected, (state) => {
      state.acceptDocumentStatus = 'failed';
    });
  },
});

export const {
  updateMigrato,
  resetLoginState,
  updateFirstAccess,
  setCodiceUtente,
  setCodiceProspect,
  setTokenProspect,
  setCodiceProspectRappresentante,
  setCurrentDocumentVersion,
  resetDocumentAcceptanceStatus,
} = loginSlice.actions;

export default loginSlice.reducer;

